/**
 * The controls on a published draft — slice 43: what Meta says now, start,
 * pause, budget, spend cap, and the history of the changes. It leads the
 * page; the composed draft folds below it.
 *
 * ## "IS IT RUNNING, AND CAN I STOP IT" COMES FIRST
 *
 * Meta's status is the page's status, and the brake is the page's primary
 * button whenever it is offered — Start when the campaign is paused, Pause
 * when it runs, never both. The budget and cap forms open below the buttons,
 * so the brake is never behind a Cancel; and pressing Pause does not wait for
 * anything else this page has in flight.
 *
 * ## START RESTATES META'S NUMBERS, AND SENDS THEM BACK
 *
 * The first press replaces the figures with the confirmation: the budget
 * drawn large, the end, what is spent, the cap, how many ads a start leaves
 * running and whose ad account pays — read off the delivery this page drew,
 * not off the draft. The confirming press sends that read's fingerprint
 * (`metaStartSeen`) with the version, and the API reads Meta again and refuses
 * if any of it moved: what starts is what was on this screen.
 *
 * ## MONEY: THE NEW FIGURE BESIDE THE OLD ONE, BEFORE THE PRESS THAT SENDS IT
 *
 * A budget or cap is typed in the currency the read reported, converted by the
 * same `typedAmountToMinor` the API uses, and shown as old → new before the
 * button that sends it is enabled. The request names the value the person saw
 * at Meta (`seen`), so a change made in Ads Manager meanwhile is refused
 * rather than overwritten. A refusal about the amount — Wend's or Meta's —
 * stays on the field, with the form open.
 *
 * ## A SENT CHANGE IS A RESULT; A REFUSED ONE IS AN ERROR
 *
 * The API answers a change it sent with `done` or `stopped`, and refuses one
 * it did not send with an error — see `MetaAdsControlService`. Done is a toast;
 * stopped is a callout that stays until the next action, because it can say
 * the campaign may be running — until a fresh read says it is not; an error is
 * the request-failure alert. Either way Meta is read again.
 *
 * ## ONE OUTAGE, ONE ALERT
 *
 * A read of Meta that fails is an error with a retry, and the record's own
 * badge beside "Not read" carries what Wend last did. A history that fails
 * alone is a chip and a retry where it would have been; both failing are one
 * error whose retry reads both.
 */
import { Badge, Button, Callout, FormField, Input, Kicker, Spinner, StateChip, buttonVariants, useToast } from '@wend/ui';
import { ExternalLink, Gauge, Pause, Play, RotateCw, Wallet } from 'lucide-react';
import { useEffect, useRef, useState, type FormEvent } from 'react';

import {
  META_ADS_GRANT_UNKNOWN,
  metaBudgetOnTwoLevels,
  metaDeliveryBudget,
  metaStartSeen,
  metaSwitchedOnBefore,
  type MetaAdControlAction,
  type MetaAdControlResult,
  type MetaAdDelivery,
  type MetaAdDraftRecord,
  type MetaAdsIntegration,
} from '@wend/shared';

import { ErrorState } from '../../components/ErrorState';
import { isApiError } from '../../lib/api/errors';
import { RetryWait, useRetryWait } from '../../lib/retry-wait';
import {
  changeMetaAdBudget,
  changeMetaAdSpendCap,
  fetchMetaAdDelivery,
  fetchMetaAdHistory,
  pauseMetaAd,
  startMetaAd,
} from '../../lib/api/meta-ads';
import { useAsyncData } from '../../lib/use-async-data';
import { formatMetaMoney } from '../integrations/MetaAdsAccountRead';
import { RequestFailureAlert, requestFailure, type RequestFailure } from '../integrations/request-failure';
import { MoreDetail } from '../templates/MoreDetail';
import { adsAStartRuns, adsManagerUrl, controlsOffered, doneTitle, stoppedTone, typedAmount } from './meta-ad-controls';
import {
  MetaAdDeliveryFigures,
  MetaAdDeliveryRefusal,
  MetaAdDeliveryStatus,
  MetaAdHistory,
  budgetLabel,
  formatInstantIn,
} from './MetaAdDelivery';
import { DraftStatusBadge, Fact, Mono } from './MetaAdDraftSummary';

type Mode = 'start' | 'budget' | 'spend_cap' | null;

interface Stopped {
  readonly action: MetaAdControlAction;
  readonly result: MetaAdControlResult;
  /** When the read the page showed as the change was sent was made — a later read can overrule the alarm. */
  readonly readAt: string | null;
}

export function MetaAdControls({
  record,
  integration,
  onChanged,
  onRefresh,
  onRead,
}: {
  record: MetaAdDraftRecord;
  integration: MetaAdsIntegration | null;
  onChanged: (record: MetaAdDraftRecord) => void;
  /** Refresh re-reads the rest of the page's Meta figures too (slice 44's results), so one read time covers it. */
  onRefresh?: () => void;
  /**
   * Meta's reading, handed up as it settles — `null` for a read that failed
   * or answered a state with no delivery in it.
   *
   * Slice 52, gap 3: the results section below now decides whether to draw
   * anything from what META says about this campaign rather than from Wend's
   * `started_at`, and this read is the one the page already makes. The
   * alternative was a second delivery read from the results section, which
   * would spend two of the workspace's thirty reads per five minutes for one
   * page and could disagree with the figures drawn directly above it.
   */
  onRead?: (delivery: MetaAdDelivery | null) => void;
}) {
  const { toast } = useToast();
  const delivery = useAsyncData((signal) => fetchMetaAdDelivery(record.id, signal), [record.id]);
  const history = useAsyncData((signal) => fetchMetaAdHistory(record.id, signal), [record.id]);
  const [mode, setMode] = useState<Mode>(null);
  const [pending, setPending] = useState<Exclude<MetaAdControlAction, 'pause'> | null>(null);
  const [pausing, setPausing] = useState(false);
  const [failure, setFailure] = useState<RequestFailure | null>(null);
  const [stopped, setStopped] = useState<Stopped | null>(null);
  const [settled, setSettled] = useState(0);
  const primary = useRef<HTMLButtonElement>(null);

  /* After every sent change, focus returns to the slot's primary button —
     Start or Pause, whichever the new read offers. */
  useEffect(() => {
    if (settled > 0) primary.current?.focus();
  }, [settled]);

  /* Handed up once per settled read. `delivery.data` is a fresh object per
     read, so this fires when a read lands and not on every render. */
  const settledRead =
    delivery.status === 'ready' && delivery.data?.state === 'ok' ? delivery.data.delivery : null;
  useEffect(() => {
    onRead?.(settledRead);
  }, [settledRead, onRead]);

  const timezone = integration?.timezoneName ?? null;
  /* A read that failed on a reload is not the read before it: a stale
     reading is never drawn as the current one, nor offered from. */
  const unread = delivery.status === 'error';
  const offered = controlsOffered(
    record,
    unread ? 'unread' : delivery.data,
    integration?.grant ?? META_ADS_GRANT_UNKNOWN,
    new Date(),
  );
  const read = !unread && delivery.data?.state === 'ok' ? delivery.data.delivery : null;
  /* Slice 52, gap 3: Wend's own first start OR a campaign Meta says has
     delivered — see `metaSwitchedOnBefore`. Wend's record alone would call a
     campaign somebody has been running in Ads Manager unstarted, and its
     "first" start would switch every ad back on. */
  const switchedOnBefore = read !== null && metaSwitchedOnBefore(record, read);
  const historyFailed = history.status === 'error';
  const bothFailed = unread && historyFailed;
  const adAccountId = record.published.adAccountId;

  /* A pause that stopped is an alarm until a read made after it says the
     campaign is not running. */
  const alarmOverruled =
    stopped !== null &&
    stopped.action === 'pause' &&
    read !== null &&
    read.readAt !== stopped.readAt &&
    !read.campaign.running;

  function reloadBoth(): void {
    delivery.reload();
    history.reload();
    /* And the page's other Meta figures, so every read on it is one read. */
    onRefresh?.();
  }

  function open(next: Exclude<Mode, null>): void {
    /* A stopped pause stays said; anything else gives way to what is opened. */
    if (stopped !== null && stopped.action !== 'pause') setStopped(null);
    setFailure(null);
    setMode(next);
  }

  /**
   * Sends one change and draws its answer. Pause is sent even while another
   * change waits on Meta — the brake does not queue.
   */
  async function run(
    action: MetaAdControlAction,
    send: () => Promise<MetaAdControlResult>,
    onAmountRefused?: (message: string) => void,
  ): Promise<void> {
    const brake = action === 'pause';
    if (brake ? pausing : pending !== null) return;
    setFailure(null);
    if (brake) setPausing(true);
    else setPending(action);
    const readAt = read?.readAt ?? null;
    try {
      const result = await send();
      onChanged(result.record);
      const amountRefused =
        result.outcome === 'stopped' &&
        result.stop === 'refused' &&
        result.message !== null &&
        (action === 'budget' || action === 'spend_cap') &&
        onAmountRefused !== undefined;
      if (amountRefused) {
        onAmountRefused(result.message ?? '');
      } else {
        if (!brake || mode === 'start') setMode(null);
        if (result.outcome === 'done') {
          setStopped(null);
          toast({ tone: 'success', title: doneTitle(action, result) });
        } else {
          setStopped({ action, result, readAt });
        }
      }
      setSettled((count) => count + 1);
    } catch (thrown) {
      const onAmount = isApiError(thrown) ? thrown.fieldErrors.find((one) => one.path === 'amount') : undefined;
      if (onAmount !== undefined && onAmountRefused !== undefined) {
        onAmountRefused(onAmount.message);
      } else {
        setFailure(requestFailure(thrown));
        /* The confirmation showed a read the API has just refused against;
           the fresh one is what to look at now. */
        if (mode === 'start') setMode(null);
      }
    } finally {
      if (brake) setPausing(false);
      else setPending(null);
      reloadBoth();
    }
  }

  return (
    <section data-role="meta-ad-controls" className="flex flex-col gap-5">
      {bothFailed ? (
        <ErrorState error={delivery.error} title="Could not read this campaign" onRetry={reloadBoth} />
      ) : unread || (delivery.data !== null && delivery.data.state !== 'ok') ? (
        /* Meta's status is not known: what Wend last did, and that it was not read. */
        <div className="flex flex-col gap-3">
          <div className="flex flex-wrap items-center gap-2">
            <DraftStatusBadge record={record} now={Date.now()} />
            <StateChip state="not_measured" label="Not read" />
          </div>
          {unread ? (
            <ErrorState error={delivery.error} title="Could not read this campaign" onRetry={delivery.reload} />
          ) : (
            delivery.data !== null && delivery.data.state !== 'ok' && <MetaAdDeliveryRefusal state={delivery.data} />
          )}
        </div>
      ) : read === null ? (
        <div className="flex items-center gap-3 text-[0.92rem] text-muted">
          <Spinner size="md" label={null} />
          <span role="status">Reading Meta…</span>
        </div>
      ) : (
        <>
          <MetaAdDeliveryStatus delivery={read} timezone={timezone} switchedOnBefore={switchedOnBefore} />
          {mode === 'start' ? (
            <StartConfirm
              delivery={read}
              integration={integration}
              timezone={timezone}
              switchedOnBefore={switchedOnBefore}
              pending={pending === 'start'}
              onCancel={() => setMode(null)}
              onStart={() => {
                void run('start', () => startMetaAd(record.id, { version: record.version, seen: metaStartSeen(read) }));
              }}
            />
          ) : (
            <MetaAdDeliveryFigures delivery={read} timezone={timezone} />
          )}
        </>
      )}

      {stopped !== null && !alarmOverruled && (
        <Callout
          data-role="meta-ad-control-stopped"
          tone={stoppedTone(stopped.action, stopped.result.stop)}
          live="assertive"
        >
          {stopped.result.message}
        </Callout>
      )}
      {failure !== null && <RequestFailureAlert failure={failure} />}

      <div data-role="meta-ad-control-actions" className="flex flex-wrap items-center gap-3">
        {offered.start && mode !== 'start' && (
          <Button ref={primary} type="button" size="sm" disabled={pending !== null} onClick={() => open('start')}>
            <Play aria-hidden="true" className="size-4" />
            Start
          </Button>
        )}
        {offered.pause && (
          <Button
            ref={primary}
            type="button"
            size="sm"
            loading={pausing}
            onClick={() => {
              void run('pause', () => pauseMetaAd(record.id));
            }}
          >
            <Pause aria-hidden="true" className="size-4" />
            Pause
          </Button>
        )}
        {offered.budget && mode === null && (
          <Button type="button" variant="secondary" size="sm" disabled={pending !== null} onClick={() => open('budget')}>
            <Wallet aria-hidden="true" className="size-4" />
            Budget
          </Button>
        )}
        {offered.spendCap && mode === null && (
          <Button type="button" variant="secondary" size="sm" disabled={pending !== null} onClick={() => open('spend_cap')}>
            <Gauge aria-hidden="true" className="size-4" />
            Spend cap
          </Button>
        )}
        {read !== null && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            loading={delivery.refreshing}
            onClick={() => {
              delivery.reload();
              onRefresh?.();
            }}
          >
            <RotateCw aria-hidden="true" className="size-4" />
            Refresh
          </Button>
        )}
        {/* Where Wend offers no control of delivery — and, since slice 52,
            where the budget is set somewhere Wend's own control does not
            write. See `controlsOffered`. */}
        {offered.adsManager && adAccountId !== null && (
          <a
            href={adsManagerUrl(adAccountId, record.published.campaignId)}
            target="_blank"
            rel="noreferrer"
            className={buttonVariants({ variant: 'secondary', size: 'sm' })}
          >
            <ExternalLink aria-hidden="true" className="size-4" />
            Ads Manager
          </a>
        )}
      </div>

      {/* Only ever the ad set's, and only when that is where the money is —
          `controlsOffered` withdrew the button otherwise, so this is the same
          condition said twice on purpose: the form is what sends the request. */}
      {mode === 'budget' && read !== null && read.adSet.budget !== null && offered.budget && (
        <AmountForm
          kind="budget"
          label={budgetLabel('ad_set', read.adSet.budget.kind)}
          currency={read.currency}
          current={read.adSet.budget.amount}
          pending={pending === 'budget'}
          onCancel={() => setMode(null)}
          onSubmit={(amount, refuse) => {
            const seen = read.adSet.budget;
            if (seen === null || amount === null) return;
            void run(
              'budget',
              () =>
                changeMetaAdBudget(record.id, {
                  amount,
                  currency: read.currency,
                  seen: { kind: seen.kind, amount: seen.amount },
                }),
              refuse,
            );
          }}
        />
      )}
      {mode === 'spend_cap' && read !== null && (
        <AmountForm
          kind="spend_cap"
          label="Spend cap"
          currency={read.currency}
          current={read.campaign.spendCap}
          pending={pending === 'spend_cap'}
          onCancel={() => setMode(null)}
          onSubmit={(amount, refuse) => {
            void run(
              'spend_cap',
              () =>
                changeMetaAdSpendCap(record.id, {
                  amount,
                  currency: read.currency,
                  seen: { spendCap: read.campaign.spendCap },
                }),
              refuse,
            );
          }}
        />
      )}

      {/* A history that did not load is said, never left out: an empty space
          here reads as "nothing was ever changed". */}
      {historyFailed && !bothFailed && (
        <HistoryNotLoaded error={history.error} onRetry={history.reload} />
      )}
      {!historyFailed && history.data !== null && history.data.events.length > 0 && (
        <MoreDetail
          summary={`History · ${String(history.data.events.length)}${history.data.truncated ? '+' : ''}`}
        >
          <MetaAdHistory history={history.data} timezone={timezone} />
        </MoreDetail>
      )}
    </section>
  );
}

/**
 * The history did not load. An empty space here reads as "nothing was ever
 * changed", so the absence is said — and when a limit refused the read, the
 * retry is disabled with the wait beside it rather than inviting a press that
 * will be refused again. See `lib/retry-wait`.
 */
function HistoryNotLoaded({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  const wait = useRetryWait(error);
  return (
    <div className="flex flex-wrap items-center gap-2" data-role="meta-ad-history-failed">
      <StateChip state="not_measured" label="History not loaded" />
      <Button type="button" variant="ghost" size="sm" disabled={wait !== null} onClick={onRetry}>
        Try again
      </Button>
      <RetryWait seconds={wait} />
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Start                                                                       */
/* -------------------------------------------------------------------------- */

function StartConfirm({
  delivery,
  integration,
  timezone,
  switchedOnBefore,
  pending,
  onCancel,
  onStart,
}: {
  delivery: MetaAdDelivery;
  integration: MetaAdsIntegration | null;
  timezone: string | null;
  /** {@link metaSwitchedOnBefore}, not Wend's record: see `adsAStartRuns`. */
  switchedOnBefore: boolean;
  pending: boolean;
  onCancel: () => void;
  onStart: () => void;
}) {
  const box = useRef<HTMLDivElement>(null);
  const { adSet, campaign } = delivery;
  const money = (minor: number) => formatMetaMoney(minor, delivery.currency) ?? delivery.currency;
  /*
    The money restated here is whichever level holds it, under that level's own
    label — the same `metaDeliveryBudget` the figures above used, and the one
    the API asks when it reads Meta again before starting. A confirmation that
    restated the ad set's null beside a campaign budget in force would be a
    confirmation of nothing. `metaStartSeen` sends the level back with the
    amount, so a budget moved between levels between this box opening and the
    press is refused by name even when the figure did not change.
  */
  const inForce = metaDeliveryBudget(delivery);
  const twoLevels = metaBudgetOnTwoLevels(delivery);

  /* Opened where it can be seen: the box replaces the figures, and on a phone
     its button would otherwise sit below the fold. */
  useEffect(() => {
    box.current?.scrollIntoView({ block: 'nearest' });
  }, []);

  return (
    <div ref={box} data-role="meta-ad-start-confirm" className="flex flex-col gap-4 rounded-base border border-line p-4">
      {/* The panel's own words for each absence: "none" is a fact, and a
          dash would read as "not known". */}
      <dl className="grid gap-x-8 gap-y-4 sm:grid-cols-2 lg:grid-cols-3">
        {twoLevels ? (
          <>
            {/* Unreachable — `twoLevels` is both levels reporting — and still
                the chip rather than a zero: this is the panel somebody reads
                immediately before spending money, and ₹0.00 for a figure that
                was never measured is the one thing it must not say. */}
            <Fact label={budgetLabel('ad_set', adSet.budget?.kind ?? null)}>
              {adSet.budget === null ? (
                <StateChip state="not_measured" label="Not read" />
              ) : (
                <span className="font-mono text-[1.25rem] leading-none tabular-nums">{money(adSet.budget.amount)}</span>
              )}
            </Fact>
            <Fact label={budgetLabel('campaign', campaign.budget?.kind ?? null)}>
              {campaign.budget === null ? (
                <StateChip state="not_measured" label="Not read" />
              ) : (
                <span className="font-mono text-[1.25rem] leading-none tabular-nums">
                  {money(campaign.budget.amount)}
                </span>
              )}
            </Fact>
          </>
        ) : (
          <Fact label={budgetLabel(inForce?.level ?? null, inForce?.budget.kind ?? null)}>
            {inForce === null ? (
              <StateChip state="not_measured" label="Not read" />
            ) : (
              <span
                data-role="meta-ad-start-confirm-budget"
                data-level={inForce.level}
                className="font-mono text-[1.25rem] leading-none tabular-nums"
              >
                {money(inForce.budget.amount)}
              </span>
            )}
          </Fact>
        )}
        <Fact label="Ends">
          {adSet.endTime === null ? (
            <Badge tone="neutral">No end date</Badge>
          ) : (
            <Mono>{formatInstantIn(adSet.endTime, timezone)}</Mono>
          )}
        </Fact>
        <Fact label="Spent">
          {delivery.spent === null ? <StateChip state="not_measured" label="Not measured" /> : <Mono>{money(delivery.spent)}</Mono>}
        </Fact>
        <Fact label="Spend cap">
          {campaign.spendCap === null ? <Badge tone="neutral">No spend cap</Badge> : <Mono>{money(campaign.spendCap)}</Mono>}
        </Fact>
        <Fact label="Ads that run">
          <Mono>
            {adsAStartRuns(delivery, switchedOnBefore)} of {delivery.ads.length}
          </Mono>
        </Fact>
        <Fact label="Ad account">{integration?.accountName ?? <Mono>{integration?.adAccountId ?? '—'}</Mono>}</Fact>
      </dl>
      <div className="flex flex-wrap items-center gap-3">
        {/* Focus moves into the box a press just opened, so a screen reader meets it. */}
        <Button type="button" size="sm" autoFocus loading={pending} onClick={onStart}>
          <Play aria-hidden="true" className="size-4" />
          {pending ? 'Starting' : 'Start delivery'}
        </Button>
        <Button type="button" variant="ghost" size="sm" disabled={pending} onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Budget and spend cap                                                        */
/* -------------------------------------------------------------------------- */

function AmountForm({
  kind,
  label,
  currency,
  current,
  pending,
  onCancel,
  onSubmit,
}: {
  kind: 'budget' | 'spend_cap';
  label: string;
  currency: string;
  current: number | null;
  pending: boolean;
  onCancel: () => void;
  /** `null` removes a spend cap. `refuse` puts a refusal about the amount on the field. */
  onSubmit: (amount: string | null, refuse: (message: string) => void) => void;
}) {
  const [typed, setTyped] = useState('');
  const [removing, setRemoving] = useState(false);
  const [refused, setRefused] = useState<string | null>(null);

  const converted = typed.trim() === '' ? null : typedAmount(typed, currency, kind === 'budget' ? 'The budget' : 'The spend cap');
  const next = removing ? null : converted?.ok === true ? converted.minor : undefined;
  /* Only a spend cap is ever `null` here: no cap, in the history's word for it. */
  const money = (minor: number | null) => (minor === null ? 'None' : (formatMetaMoney(minor, currency) ?? currency));
  const ready = removing || (next !== undefined && next !== current);
  const error = refused ?? (converted !== null && !converted.ok ? converted.message : undefined);

  function submit(event: FormEvent): void {
    event.preventDefault();
    if (!ready || pending) return;
    setRefused(null);
    onSubmit(removing ? null : typed.trim(), setRefused);
  }

  return (
    <form
      data-role={kind === 'budget' ? 'meta-ad-budget-form' : 'meta-ad-spend-cap-form'}
      onSubmit={submit}
      className="flex flex-col gap-4 rounded-base border border-line p-4"
    >
      {!removing && (
        <FormField label={`${label}, ${currency}`} error={error}>
          <Input
            data-role="meta-ad-amount"
            value={typed}
            inputMode="decimal"
            className="max-w-[16rem] font-mono"
            autoFocus
            disabled={pending}
            onChange={(event) => {
              setTyped(event.target.value);
              setRefused(null);
            }}
          />
        </FormField>
      )}
      {removing && <Kicker as="span">{label}</Kicker>}
      {ready && (
        <p data-role="meta-ad-amount-preview" className="font-mono text-[0.92rem] tabular-nums text-text">
          {money(current)} → {money(next ?? null)}
        </p>
      )}
      {removing && refused !== null && (
        /* With no amount field on screen, the refusal is said where the field was. */
        <p role="alert" className="text-[0.85rem] text-bad">
          {refused}
        </p>
      )}
      <div className="flex flex-wrap items-center gap-3">
        <Button type="submit" size="sm" loading={pending} disabled={!ready}>
          {kind === 'budget' ? 'Set budget' : removing ? 'Remove spend cap' : 'Set spend cap'}
        </Button>
        {kind === 'spend_cap' && current !== null && !removing && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={pending}
            onClick={() => {
              setRemoving(true);
              setRefused(null);
            }}
          >
            Remove cap
          </Button>
        )}
        <Button type="button" variant="ghost" size="sm" disabled={pending} onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
}
